import { apiService } from '../services/apiService';
import {
  AlertConfiguration,
  AlertSlotMonitorRow,
  AlertSlotState,
} from '../types/dashboard';
import { effectiveAlertKindFromConfig } from './alertConfigurationListRows';

/** Build slot monitor rows from alert configs + per-alert trigger-status (fallback when overview omits monitor). */
export async function buildAlertSlotMonitorFromConfigs(
  configs: AlertConfiguration[],
  token?: string
): Promise<AlertSlotMonitorRow[]> {
  const multi = configs.filter(
    (c) =>
      (c.is_active ?? true) &&
      c.is_multi_trigger &&
      (c.trigger_count ?? 0) > 0 &&
      Array.isArray(c.slot_sources) &&
      (c.slot_sources?.length ?? 0) === (c.trigger_count ?? 0)
  );

  const rows: AlertSlotMonitorRow[] = [];

  for (const c of multi) {
    const rawId = c.id;
    const alertId =
      typeof rawId === 'number' ? rawId : typeof rawId === 'string' ? parseInt(rawId, 10) : 0;
    if (!alertId) continue;

    const stRes = await apiService.getTriggerStatus(alertId, token);
    if (!stRes.success || !stRes.data?.is_multi_trigger) continue;

    const st = stRes.data;
    const tc = st.trigger_count ?? c.trigger_count ?? 0;
    const kind =
      st.alert_kind ??
      st.alertKind ??
      effectiveAlertKindFromConfig(c);
    const slotSources = st.slot_sources?.length
      ? st.slot_sources
      : c.slot_sources ?? [];

    let slots: AlertSlotState[] = [];
    let consensus: AlertSlotMonitorRow['consensus'] = null;

    const dirs = st.slot_directions ?? st.slotDirections;
    const isBmta =
      kind === 'bidirectional_multi' ||
      (Array.isArray(dirs) && dirs.length > 0);

    if (isBmta) {
      const slotDirs = dirs ?? [];
      slots = Array.from({ length: tc }, (_, i) => {
        const v = (slotDirs[i] ?? 'neutral').toLowerCase();
        if (v === 'up' || v === 'down') return v;
        return 'neutral';
      });
      if (slots.length && slots.every((s) => s === 'up')) consensus = 'all_up';
      else if (slots.length && slots.every((s) => s === 'down')) consensus = 'all_down';
    } else {
      const active = new Set(st.active_slots ?? []);
      slots = Array.from({ length: tc }, (_, i) =>
        active.has(i) ? 'active' : 'inactive'
      );
      if (tc > 0 && active.size >= tc) consensus = 'all_active';
    }

    rows.push({
      alertId,
      stockSymbol: c.stock_symbol ?? c.stockSymbol ?? '',
      positionType: c.position_type ?? c.positionType ?? '',
      alertKind: isBmta ? 'bidirectional_multi' : kind,
      triggerCount: tc,
      slotSources,
      slotDescriptions: Array.isArray(c.slot_descriptions) ? c.slot_descriptions : [],
      slots,
      consensus,
      lastTriggeredAt: c.last_triggered_at ?? undefined,
    });
  }

  return rows.sort((a, b) => a.stockSymbol.localeCompare(b.stockSymbol));
}
